import { useState, useEffect, useRef } from 'react';

export interface ScreenshotItem {
  src: string;
  alt: string;
  title: string;
  description: string;
  step: string;
}

interface ScreenshotCarouselProps {
  screenshots: ScreenshotItem[];
  variant?: 'mobile' | 'desktop';
  colorScheme?: {
    badge: string;
    activeBadge: string;
    border: string;
    ring: string;
    indicator: string;
  };
  thumbnailTitle?: string;
  showThumbnails?: boolean;
  autoplay?: boolean | 'auto';
}

const ScreenshotCarousel = ({
  screenshots,
  variant = 'mobile',
  colorScheme = {
    badge: 'bg-blue-100 text-blue-800',
    activeBadge: 'bg-blue-600',
    border: 'border-blue-600',
    ring: 'ring-blue-200',
    indicator: 'bg-blue-600',
  },
  thumbnailTitle,
  showThumbnails = false,
  autoplay = false,
}: ScreenshotCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const autoplayIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  const stopAutoplay = () => {
    if (autoplayIntervalRef.current) {
      clearInterval(autoplayIntervalRef.current);
      autoplayIntervalRef.current = null;
    }
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    stopAutoplay();
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + screenshots.length) % screenshots.length);
    stopAutoplay();
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % screenshots.length);
    stopAutoplay();
  };

  // Gestion du swipe (mobile-first)
  const minSwipeDistance = 50; // Distance minimale en pixels pour déclencher un swipe

  const onTouchStart = (e: React.TouchEvent) => {
    touchEndX.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
    touchStartY.current = e.targetTouches[0].clientY;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
    
    // Empêcher le scroll vertical si on détecte un mouvement horizontal
    if (touchStartX.current !== null && touchStartY.current !== null) {
      const deltaX = Math.abs(e.targetTouches[0].clientX - touchStartX.current);
      const deltaY = Math.abs(e.targetTouches[0].clientY - touchStartY.current);
      
      // Si le mouvement horizontal est plus important que le vertical, empêcher le scroll
      if (deltaX > deltaY && deltaX > 10) {
        e.preventDefault();
      }
    }
  };

  const onTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }

    // Reset
    touchStartX.current = null;
    touchStartY.current = null;
    touchEndX.current = null;
  };

  // Gestion de l'autoplay
  useEffect(() => {
    // Nettoyer l'interval précédent s'il existe
    if (autoplayIntervalRef.current) {
      clearInterval(autoplayIntervalRef.current);
      autoplayIntervalRef.current = null;
    }

    // Démarrer l'autoplay si activé
    if (autoplay === true || autoplay === 'auto') {
      autoplayIntervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % screenshots.length);
      }, 3000); // Change de slide toutes les 3 secondes
    }

    // Cleanup function
    return () => {
      if (autoplayIntervalRef.current) {
        clearInterval(autoplayIntervalRef.current);
        autoplayIntervalRef.current = null;
      }
    };
  }, [autoplay, screenshots.length]);

  return (
    <div className="space-y-12 overflow-x-hidden">
      {/* Carousel Principal */}
      <div className="relative w-full overflow-x-hidden">
        <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-12 overflow-hidden">
          {/* Contrôles de navigation */}
          <button
            onClick={goToPrevious}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-gray-700 p-2 sm:p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
            aria-label="Image précédente"
          >
            <svg className="w-4 h-4 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          
          <button
            onClick={goToNext}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white text-gray-700 p-2 sm:p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
            aria-label="Image suivante"
          >
            <svg className="w-4 h-4 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Contenu du carousel */}
          <div className="relative max-w-7xl mx-auto w-full overflow-x-hidden">
            <div className="overflow-x-hidden">
              <div 
                className="flex transition-transform duration-500 ease-in-out will-change-transform select-none" 
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                {screenshots.map((screenshot, index) => (
                  <div key={index} className="min-w-full flex-shrink-0 w-full">
                    <div className="grid md:grid-cols-2 gap-6 lg:gap-8 items-center px-4 sm:px-6 w-full">
                      {/* Device Frame */}
                      <div className="relative flex justify-center">
                        {variant === 'mobile' ? (
                          <MobileFrame screenshot={screenshot} />
                        ) : (
                          <DesktopFrame screenshot={screenshot} />
                        )}
                      </div>

                      {/* Description */}
                      <div className="text-center md:text-left mt-6 md:mt-0 max-w-full">
                        <div className={`inline-block ${colorScheme.badge} px-3 py-1 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4`}>
                          {screenshot.step}
                        </div>
                        <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 sm:mb-4 break-words">
                          {screenshot.title}
                        </h3>
                        <p className="text-sm sm:text-base md:text-lg text-gray-600 leading-relaxed mb-4 sm:mb-6 break-words">
                          {screenshot.description}
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-2 sm:gap-3 flex-wrap">
                          <span className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">Étape {index + 1} sur {screenshots.length}</span>
                          <div className="flex gap-1">
                            {screenshots.map((_, i) => (
                              <div
                                key={i}
                                className={`h-2 rounded-full transition-all duration-300 ${
                                  i === currentIndex ? `w-8 ${colorScheme.indicator}` : 'w-2 bg-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Grille de miniatures */}
      {showThumbnails && (
        <div className='hidden lg:block'>
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 text-center">
            {thumbnailTitle || `Parcours complet en ${screenshots.length} étapes`}
          </h3>
          <div 
            className="grid gap-3 sm:gap-4"
            style={{ 
              gridTemplateColumns: `repeat(${Math.min(screenshots.length, 8)}, minmax(0, 1fr))`,
              display: 'grid'
            }}
          >
            {screenshots.map((screenshot, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`group relative bg-black rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-2 ${
                  currentIndex === index ? `${colorScheme.border} ring-2 ${colorScheme.ring}` : 'border-gray-300'
                } p-1.5 sm:p-2`}
                aria-label={`Aller à l'étape ${index + 1}`}
              >
                {variant === 'mobile' ? (
                  <MobileThumbnail screenshot={screenshot} index={index} />
                ) : (
                  <DesktopThumbnail screenshot={screenshot} index={index} />
                )}
                {/* Badge d'étape active */}
                {currentIndex === index && (
                  <div className={`absolute -top-1 -right-1 ${colorScheme.activeBadge} text-white text-xs font-bold rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center shadow-lg`}>
                    {index + 1}
                  </div>
                )}
              </button>
            ))}
          </div>
          <p className="text-center text-gray-600 mt-4 text-xs sm:text-sm">
            Cliquez sur une miniature pour voir l'étape correspondante
          </p>
        </div>
      )}
    </div>
  );
};

// Mobile Frame Component
const MobileFrame = ({ screenshot }: { screenshot: ScreenshotItem }) => {
  return (
    <div className="relative mx-auto transform hover:scale-105 transition-transform duration-300" style={{ width: 'clamp(200px, 30vw, 350px)' }}>
      <div className="relative bg-black rounded-[3rem] p-2.5 sm:p-3 md:p-3.5 shadow-2xl" style={{ aspectRatio: '9/19.5' }}>
        {/* Encoche (notch) */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 bg-black rounded-b-2xl z-10" style={{ width: '35%', height: '4%', maxWidth: '120px', maxHeight: '24px' }}></div>
        
        {/* Écran */}
        <div className="relative bg-white rounded-[2.5rem] overflow-hidden" style={{ height: 'calc(100% - 0px)' }}>
          <div className="relative overflow-hidden" style={{ height: '100%' }}>
            <img
              src={screenshot.src}
              alt={screenshot.alt}
              className="w-full h-full"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const placeholder = target.nextElementSibling as HTMLElement;
                if (placeholder) placeholder.style.display = 'flex';
              }}
            />
            <div
              className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 flex flex-col items-center justify-center text-gray-500"
              style={{ display: 'none' }}
            >
              <svg className="w-12 h-12 mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-xs font-medium">Capture d'écran</p>
            </div>
          </div>
          
          {/* Barre de navigation (home indicator) */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-white/30 rounded-full z-20" style={{ width: '35%', height: '2px', maxWidth: '120px' }}></div>
        </div>
      </div>
    </div>
  );
};

// Desktop Frame Component
const DesktopFrame = ({ screenshot }: { screenshot: ScreenshotItem }) => {
  return (
    <div className="relative mx-auto transform hover:scale-105 transition-transform duration-300" style={{ width: 'clamp(300px, 50vw, 700px)' }}>
      <div className="relative bg-gray-800 rounded-t-2xl shadow-2xl overflow-hidden" style={{ aspectRatio: '13/10' }}>
        {/* Barre supérieure du navigateur */}
        <div className="absolute top-0 left-0 right-0 bg-gray-700 z-20 flex items-center px-4 py-2" style={{ height: '40px' }}>
          <div className="flex gap-2 mr-4">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <div className="flex-1 bg-gray-600 rounded px-3 py-1">
            <div className="flex items-center gap-2">
              <svg className="w-3 h-3 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span className="text-xs text-gray-300 truncate">admin.parking.tn</span>
            </div>
          </div>
        </div>
        
        {/* Écran avec image */}
        <div className="relative bg-white overflow-hidden flex items-center justify-center" style={{ height: '100%', paddingTop: '40px' }}>
          <img
            src={screenshot.src}
            alt={screenshot.alt}
            className="w-full h-full object-contain"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const placeholder = target.nextElementSibling as HTMLElement;
              if (placeholder) placeholder.style.display = 'flex';
            }}
          />
          <div
            className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 flex flex-col items-center justify-center text-gray-500"
            style={{ display: 'none', paddingTop: '40px' }}
          >
            <svg className="w-12 h-12 mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-xs font-medium">Capture d'écran</p>
          </div>
        </div>
      </div>
      
      {/* Base de l'écran */}
      <div className="relative mx-auto bg-gray-800 rounded-b-3xl" style={{ width: '100%', height: '20px', marginTop: '-2px', marginLeft: '0' }}>
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-32 h-4 bg-gray-900 rounded-b-lg"></div>
      </div>
    </div>
  );
};

// Mobile Thumbnail Component
const MobileThumbnail = ({ screenshot, index }: { screenshot: ScreenshotItem; index: number }) => {
  return (
    <div className="relative bg-black rounded-xl overflow-hidden p-1">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 bg-black rounded-b-lg z-10" style={{ width: '35%', height: '8px' }}></div>
      <div className="relative bg-white rounded-lg overflow-hidden" style={{ aspectRatio: '9/16' }}>
        <img
          src={screenshot.src}
          alt={screenshot.alt}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all duration-300 flex items-center justify-center">
          <div className="bg-white rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center font-bold text-gray-900 text-xs sm:text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            {index + 1}
          </div>
        </div>
      </div>
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-white/30 rounded-full z-10" style={{ width: '35%', height: '2px' }}></div>
    </div>
  );
};

// Desktop Thumbnail Component
const DesktopThumbnail = ({ screenshot, index }: { screenshot: ScreenshotItem; index: number }) => {
  return (
    <div className="relative bg-gray-800 rounded-xl overflow-hidden">
      <div className="absolute top-0 left-0 right-0 bg-gray-700 z-10 flex items-center px-2 py-1" style={{ height: '15%' }}>
        <div className="flex gap-1">
          <div className="w-1.5 h-1.5 rounded-full bg-red-500"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-yellow-500"></div>
          <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
        </div>
      </div>
      <div className="aspect-video relative overflow-hidden rounded-lg bg-white" style={{ marginTop: '15%' }}>
        <img
          src={screenshot.src}
          alt={screenshot.alt}
          className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all duration-300 flex items-center justify-center">
          <div className="bg-white rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center font-bold text-gray-900 text-xs sm:text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            {index + 1}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScreenshotCarousel;

